import {
	registerCustomElement,
	create,
	createInput,
	uuid,
	showNestedFieldsets,
	dispatchResizeDebounced,
} from '../utils.mjs';
import { ArrayInput } from './array.mjs';
import './merge-array.scss';
import FormanConfig from '../formanConfig.mjs';
import { I18n } from '../helpers/i18n.mjs';
import { Input } from './input.mjs';
import EVENTS from '../events.mjs';
import { createDragAndDrop } from '../drag-and-drop.mjs';

interface MergeArrayValue {
	branchName: BranchLabel;
	value: string;
}

interface MergeArrayOutputItem {
	type: 'condition' | 'else';
	value: string;
}

interface MergeArrayOutputValue {
	output_name: string;
	outputs: MergeArrayOutputItem[];
}

interface BranchDescriptor {
	type: 'condition' | 'else';
	order?: number;
}

interface MergeArrayInstructions {
	label?: string;
	spec: FormanConfig[];
	name?: string;
	help?: string;
	required?: boolean;
	default?: MergeArrayValue[];
	validate?: (value: MergeArrayValue[]) => boolean | string;
	editable?: boolean;
	branches?: BranchDescriptor[];
}

interface MergeArrayItemMetadata {
	context?: unknown;
	restore?: {
		value?: unknown;
	};
}

interface BranchLabel {
	text: string;
	isElse: boolean;
}

class MergeArrayItem extends HTMLElement {
	private _textInput: Input | null = null;
	private _index: number = 0;
	private _parentArrayName: string = '';

	get index(): number {
		return this._index;
	}

	get path(): string {
		return `${this._parentArrayName}[${this.index}]`;
	}

	async build(
		value: MergeArrayValue,
		index: number = 0,
		parentArrayName: string = '',
		metadata: MergeArrayItemMetadata = {},
	): Promise<void> {
		this._index = index;
		this._parentArrayName = parentArrayName;
		this.setAttribute('type', 'array-item');

		const formGroup = create('div.form-group');
		this.appendChild(formGroup);

		const branchLabel = create('span.branch-label') as HTMLSpanElement;

		const labelText = create('span.green-label') as HTMLSpanElement;
		labelText.textContent = value.branchName.text;
		branchLabel.appendChild(labelText);

		const labelSuffix = create('span.label-suffix') as HTMLSpanElement;
		labelSuffix.textContent = value.branchName.isElse ? '' : I18n.l('mergeArray.isTrue');
		branchLabel.appendChild(labelSuffix);

		formGroup.appendChild(branchLabel);

		this._textInput = createInput('text') as Input;
		formGroup.appendChild(this._textInput);

		// Pass metadata to nested input for proper restore and context handling
		const itemMetadata = metadata.restore?.value ? { ...metadata, restore: metadata.restore.value } : metadata;

		await this._textInput.build(
			{
				type: 'text',
				label: I18n.l('mergeArray.outputValue'),
				name: 'value',
				required: true,
				domain: `merge_branch_${index}`,
			},
			value?.value,
			itemMetadata,
		);
	}

	// Expose _input for compatibility with ArrayInput.validate()
	get _input(): Input | null {
		return this._textInput;
	}

	get value(): string {
		return this._textInput?.value ?? '';
	}

	setBranchLabel(label: BranchLabel) {
		const labelText = this.querySelector('.green-label');
		if (labelText) {
			labelText.textContent = label.text;
		}

		const labelSuffix = this.querySelector('.label-suffix');
		if (labelSuffix) {
			labelSuffix.textContent = label.isElse ? '' : I18n.l('mergeArray.isTrue');
		}
	}

	getState(): unknown {
		return this._textInput?.getState?.();
	}
}

interface MergeArrayMetadata {
	context?: unknown;
	restore?: {
		items?: unknown[];
		mode?: string;
	};
	fieldset?: {
		value?: object;
		metadata?: object;
	};
}

let destroyMergeArrayDnD: (() => void) | null = null;
let mergeArrayDnDRefCount = 0;

export class MergeArrayInput extends ArrayInput {
	public override _list: HTMLDivElement;
	private _removeBtn!: HTMLButtonElement;
	private _dragHandle!: HTMLDivElement;
	private _expander!: HTMLDivElement;
	private _nameInput!: Input;
	private _addOutputBtn!: HTMLElement;
	private _isCollapsed: boolean = false;
	private _wasExpandedBeforeDrag: boolean = false;
	private _arrayName: string = '';
	private _metadata: MergeArrayMetadata = {};
	private _mergeInstructions: MergeArrayInstructions | null = null;
	private _branches: BranchDescriptor[] = [{ type: 'condition', order: 1 }, { type: 'else' }];

	constructor() {
		super();
		this._itemSelector = 'imt-input-mergearray-item';
		this._list = create('div.list') as HTMLDivElement;
	}

	override connectedCallback(): void {
		super.connectedCallback();
		mergeArrayDnDRefCount++;
		if (mergeArrayDnDRefCount === 1) {
			destroyMergeArrayDnD = initMergeArrayDragAndDrop();
		}
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		mergeArrayDnDRefCount--;
		if (mergeArrayDnDRefCount <= 0) {
			destroyMergeArrayDnD?.();
			destroyMergeArrayDnD = null;
			mergeArrayDnDRefCount = 0;
		}
	}

	override get hasModeSwitch(): boolean {
		return false;
	}

	private _getLabel(index: number): BranchLabel {
		const branch = this._branches[index];
		const isElse = branch?.type === 'else';
		const position = branch?.order ?? index + 1;
		return {
			text: isElse
				? I18n.l('mergeArray.else')
				: I18n.l('mergeArray.ifConditionTrue', { position: this.getPosition(position) }),
			isElse,
		};
	}

	handleRemoveButton = (e: Event) => {
		e.stopPropagation();
		const parent = this.parentElement;
		if (parent) {
			this.remove();
			MergeArrayInput.updateDragHandleVisibility(parent);
		}
	};

	handleAddOutput = async (e: Event) => {
		e.preventDefault();
		e.stopPropagation();

		// Find fieldset from event target (works even if `this` is detached)
		const addBtn = (e.target as HTMLElement).closest('.add-output-btn');
		const fieldset = addBtn?.closest('imt-fieldset');
		if (!fieldset) return;

		const existingMergeArrays = fieldset.querySelectorAll<MergeArrayInput>('imt-input-mergearray');
		const firstMergeArray = existingMergeArrays[0];

		// Get context from any existing merge-array, or use stored metadata
		const contextSource = firstMergeArray ?? this;
		const context = contextSource._metadata?.context;
		const label = contextSource._mergeInstructions?.label;

		if (contextSource.hasAttribute('readonly') || contextSource.hasAttribute('disabled')) return;

		const newMergeArray = createInput('mergearray') as MergeArrayInput | null;
		if (!newMergeArray) return;

		// Insert before first existing, or after add button if none exist
		if (firstMergeArray) {
			firstMergeArray.before(newMergeArray);
		} else {
			addBtn?.after(newMergeArray);
		}

		await newMergeArray.build(
			{
				type: 'mergearray',
				name: `mergearray${existingMergeArrays.length + 1}`,
				label,
				branches: contextSource._mergeInstructions?.branches,
			},
			undefined,
			{ context },
		);

		MergeArrayInput.updateDragHandleVisibility(newMergeArray.parentElement);
	};

	override async _build(
		instructions: MergeArrayInstructions,
		value?: MergeArrayValue[] | MergeArrayOutputValue,
		metadata: MergeArrayMetadata = {},
	): Promise<void> {
		this.id = uuid();
		this._arrayName = instructions.name || '';
		this._metadata = metadata;
		this._mergeInstructions = instructions;
		if (instructions.branches?.length) {
			this._branches = instructions.branches;
		}
		this.classList.add('merge-array');
		this.setAttribute('type', 'array');

		// Hide the standard label created by Input.build() - we'll use Output name as the header
		if (this._label) {
			this._label.style.display = 'none';
		}

		// Add "Add another output" link to fieldset (only once, before first merge-array)
		const fieldset = this.closest('imt-fieldset');
		const isFirst = !fieldset || fieldset.querySelector('imt-input-mergearray') === this;
		if (isFirst && fieldset && !fieldset.querySelector('.add-output-btn')) {
			this._addOutputBtn = create('imt-input-array-button.add-output-btn') as HTMLElement;
			const addButton = create('button[type="button"]') as HTMLButtonElement;
			addButton.textContent = I18n.l('mergeArray.addAnotherOutput');
			addButton.addEventListener('click', this.handleAddOutput);
			this._addOutputBtn.appendChild(addButton);
			this.before(this._addOutputBtn);
		}

		this._removeBtn = create('button.form-remover[type="button"]') as HTMLButtonElement;
		this._removeBtn.addEventListener('click', this.handleRemoveButton);

		this.appendChild(this._list);

		// Determine if we're restoring from saved MergeArrayOutputValue or building fresh
		let outputName = '';
		let itemValues: MergeArrayValue[];

		if (value && typeof value === 'object' && 'output_name' in value && 'outputs' in value) {
			// Restoring from saved MergeArrayOutputValue
			const savedValue = value as MergeArrayOutputValue;
			outputName = savedValue.output_name;
			itemValues = savedValue.outputs.map((output, index) => ({
				branchName: this._getLabel(index),
				value: output.value,
			}));
		} else if (Array.isArray(value)) {
			// Building from MergeArrayValue[] (legacy format)
			itemValues = value;
		} else {
			// Default: create items based on branches
			itemValues = this._branches.map((_branch, index) => ({
				branchName: this._getLabel(index),
				value: '',
			}));
		}

		this._nameInput = createInput('text') as Input;
		this._list.prepend(this._nameInput);

		await this._nameInput.build(
			{
				type: 'text',
				label: I18n.l('mergeArray.outputName'),
				name: `${this.id}.output_name`,
				required: true,
			},
			outputName,
			{ context: metadata.context },
		);

		this._dragHandle = create('div.form-drag-drop') as HTMLDivElement;

		if (this._nameInput._actions) {
			this._nameInput._actions.appendChild(this._removeBtn);
			this._nameInput._actions.appendChild(this._dragHandle);
		}
		if (this._nameInput._label) {
			this._nameInput._label.addEventListener('click', this.toggleCollapse);
		}

		// Override expander to use our custom collapse instead of Forman's default
		this._expander = this._nameInput.querySelector('.form-expander') as HTMLDivElement;
		if (this._expander) {
			this._expander.addEventListener('click', this.toggleCollapse, true);
		}

		const itemsRestore = metadata.restore?.items;

		for (let i = 0; i < itemValues.length; i++) {
			const itemRestore = Array.isArray(itemsRestore) ? itemsRestore[i] : undefined;
			const itemMetadata: MergeArrayItemMetadata = {
				context: metadata.context,
				restore: itemRestore as MergeArrayItemMetadata['restore'],
			};
			await this._addMergeItem(itemValues[i], i, itemMetadata);
		}

		const items = this._list.querySelectorAll<MergeArrayItem>('imt-input-mergearray-item');
		items.forEach((item: MergeArrayItem, index: number) => {
			item.setBranchLabel(this._getLabel(index));
		});

		showNestedFieldsets(this, metadata?.fieldset?.value, metadata?.fieldset?.metadata);

		MergeArrayInput.updateDragHandleVisibility(this.parentElement);
	}

	override get value(): MergeArrayOutputValue {
		if (!this._list) {
			return {
				output_name: '',
				outputs: [],
			};
		}

		const items = this._list.querySelectorAll<MergeArrayItem>('imt-input-mergearray-item');

		const outputs: MergeArrayOutputItem[] = [];
		items.forEach((item, index) => {
			const isElse = this._branches[index]?.type === 'else';
			outputs.push({
				type: isElse ? 'else' : 'condition',
				value: item.value,
			});
		});

		return {
			output_name: this._nameInput?.value ?? '',
			outputs,
		};
	}

	override getState() {
		const baseState = super.getState();

		if (!this._list) {
			return { ...baseState, items: [] };
		}

		// Override items from super.getState() with our own merge array items
		const items = this._list.querySelectorAll<MergeArrayItem>('imt-input-mergearray-item');
		const itemStates = Array.from(items)
			.map((item) => item.getState())
			.filter(Boolean);

		return {
			...baseState,
			items: itemStates,
		};
	}

	override validate(): boolean {
		this.untouched = false;
		let allChildrenValid = true;

		if (this._nameInput) {
			this._nameInput.untouched = false;
			if (this._nameInput.validate) {
				if (!this._nameInput.validate()) {
					allChildrenValid = false;
				}
			}
		}

		const items = this._list.querySelectorAll<MergeArrayItem>('imt-input-mergearray-item');
		items.forEach((item) => {
			if (item._input) {
				item._input.untouched = false;
				if (item._input.validate) {
					if (!item._input.validate()) {
						allChildrenValid = false;
					}
				}
			}
		});

		this._runValidators();

		return this.valid && allChildrenValid;
	}

	toggleCollapse = (event: Event): void => {
		event.stopImmediatePropagation();
		event.preventDefault();

		this._isCollapsed = !this._isCollapsed;
		this._setCollapsed(this._isCollapsed);
	};

	collapseForDrag(): void {
		if (!this.classList.contains('items-collapsed')) {
			this._wasExpandedBeforeDrag = true;
			this._setCollapsed(true);
		}
	}

	restoreAfterDrag(): void {
		if (this._wasExpandedBeforeDrag) {
			this._setCollapsed(false);
			this._wasExpandedBeforeDrag = false;
		}
	}

	// Use custom class to avoid forman's generic .collapsed rule that hides entire .list
	private _setCollapsed(collapsed: boolean): void {
		this.classList.toggle('items-collapsed', collapsed);
		this._nameInput?.classList.toggle('items-collapsed', collapsed);
	}

	private async _addMergeItem(value: MergeArrayValue, index: number, metadata: MergeArrayItemMetadata = {}) {
		const item = document.createElement('imt-input-mergearray-item') as MergeArrayItem;
		this._list.appendChild(item);
		await item.build(value, index, this._arrayName, metadata);
	}

	static updateDragHandleVisibility(container: Element | null): void {
		if (!container) return;
		const mergeArrays = container.querySelectorAll<MergeArrayInput>(':scope > imt-input-mergearray');
		const show = mergeArrays.length >= 2;
		mergeArrays.forEach((ma) => {
			if (ma._dragHandle) {
				ma._dragHandle.style.display = show ? '' : 'none';
			}
		});
	}

	private getPosition(number: number): string {
		const text = ['th', 'st', 'nd', 'rd'];
		const value = number % 100;
		return number + (text[(value - 20) % 10] || text[value] || text[0]);
	}
}

registerCustomElement('imt-input-mergearray-item', MergeArrayItem);
registerCustomElement('imt-input-mergearray', MergeArrayInput);

function initMergeArrayDragAndDrop() {
	return createDragAndDrop({
		handleSelector: 'div.form-drag-drop',
		findDragged: (btn: Element) => btn.closest('imt-input-mergearray'),
		findContainer: (el: Element) => {
			const parent = el.parentElement;
			return parent?.querySelector(':scope > imt-input-mergearray') ? parent : null;
		},
		canDrag: (el: Element) => !el.hasAttribute('readonly') && !el.hasAttribute('disabled'),
		getItems: (container: Element) =>
			Array.from(container.querySelectorAll<MergeArrayInput>(':scope > imt-input-mergearray')),
		getIndex: (el: Element, items: Element[]) => items.indexOf(el),
		previewClass: 'forman-array-dragging-preview',
		dropZoneClass: 'drop-zone-output',
		cleanPreview: (clone: Element) =>
			clone.querySelectorAll('imt-input-mergearray-item, .form-control, imt-fieldset').forEach((el) => el.remove()),
		onDragStart: (e: MouseEvent, draggedEl: MergeArrayInput, container: Element, items: MergeArrayInput[]) => {
			// Capture preview offset BEFORE collapse so it aligns with cursor
			const preRect = draggedEl.getBoundingClientRect();
			const offsets = {
				topOffset: preRect.top - e.clientY,
				leftOffset: preRect.left - e.clientX + 18,
				width: preRect.width,
			};
			// Collapse all merge-arrays during drag for cleaner visual
			items.forEach((ma) => ma.collapseForDrag());
			// Resize panel after collapsing
			container.dispatchEvent(new CustomEvent(EVENTS.FORM.RESIZE, { bubbles: true }));
			return offsets;
		},
		moveItem: (draggedEl: Element, newIndex: number, container: Element) => {
			const items = Array.from(container.querySelectorAll<MergeArrayInput>(':scope > imt-input-mergearray'));
			const currentIndex = items.indexOf(draggedEl as MergeArrayInput);
			if (currentIndex > newIndex) {
				items[newIndex].before(draggedEl);
			} else if (currentIndex < newIndex) {
				items[newIndex].after(draggedEl);
			}
		},
		onCleanup: (_draggedEl: Element, container: Element) => {
			const all = container.querySelectorAll<MergeArrayInput>(':scope > imt-input-mergearray');
			all.forEach((ma) => ma.restoreAfterDrag());
			dispatchResizeDebounced(container);
		},
	});
}
